/**
 * Sadhana Types
 * Based on actual backend implementation in base-services
 * Points-based system: 0, 5, 10, 15, 20, 25, 30 points for each activity
 */

/**
 * Single day's sadhana data within a weekly card
 */
export interface WeeklySadhanaEntry {
  id: number;
  day: number; // 0=Monday, 6=Sunday
  date: string; // YYYY-MM-DD
  
  // Chanting Section - Rounds (0-192)
  chantingQualityRounds?: number | null;
  
  // Body Section - Points (0, 5, 10, 15, 20, 25, 30)
  bodyToBed?: number | null;
  bodyEarlyMorning?: number | null;
  bodyDaySleep?: number | null;
  bodyJapaRounds?: number | null;
  bodyMangalaArti?: number | null;
  
  // Soul Section - Points (0, 5, 10, 15, 20, 25, 30)
  soulJapaQuality?: number | null;
  soulBookReading?: number | null;
  soulLectureHearing?: number | null;
  soulWeeklyClass?: number | null;
  
  // Duty Section - Points (0, 5, 10, 15, 20, 25, 30)
  dutyDeityWorship?: number | null;
  dutyAttendance?: number | null;
  
  // Seva (Service) - Minutes (not points)
  seva1Minutes?: number | null;
  seva1Description?: string | null; // max 500 chars
  seva2Minutes?: number | null;
  seva2Description?: string | null; // max 500 chars
  seva3Minutes?: number | null;
  seva3Description?: string | null; // max 500 chars
  seva4Minutes?: number | null;
  seva4Description?: string | null; // max 500 chars
  
  // Daily summary
  dailyRemarks?: string | null; // max 1000 chars
  
  // Calculated fields (read-only)
  sevaTotalMinutes: number;
  totalPoints: number;
  
  // Timestamps
  createdAt: string;
  updatedAt: string;
}

/**
 * Complete weekly sadhana card with all entries and statistics
 */
export interface WeeklySadhanaCard {
  id: number;
  memberId: number;
  weekStartDate: string; // ISO date-time
  weekEndDate: string; // ISO date-time
  
  // Submission
  submittedAt?: string | null; // ISO date-time
  
  // Mentor feedback
  mentorComment?: string | null; // max 1000 chars
  mentorCommentedAt?: string | null; // ISO date-time
  mentorCommentedBy?: number | null;
  
  // Phase 2 new statistics (calculated on-the-fly)
  isSubmitted: boolean;
  weeklyStreak: number; // consecutive weeks submitted
  bestDay: number; // day with highest points (0-6)
  dailyAveragePoints: number; // float
  daysEntered?: number; // Number of days with entries (0-7) - derived from entries.length
  
  // Daily entries (0-7 entries)
  entries: WeeklySadhanaEntry[];
  
  // Timestamps
  createdAt: string;
  updatedAt: string;
}

/**
 * Abbreviated card info for list views (without full entries)
 */
export interface WeeklySadhanaCardSummary {
  id: number;
  memberId: number;
  weekStartDate: string;
  weekEndDate: string;
  submittedAt?: string | null;
  mentorComment?: string | null;
  mentorCommentedAt?: string | null;
  mentorCommentedBy?: number | null;
  isSubmitted: boolean;
  weeklyStreak: number;
  bestDay: number;
  dailyAveragePoints: number;
  daysEntered?: number; // Number of days with entries (0-7) - derived from entries.length
}

/**
 * Input for creating/updating daily sadhana entry
 */
export interface WeeklySadhanaEntryInput {
  memberId?: number; // for mentor/admin submissions
  date: string; // YYYY-MM-DD (required)
  
  // Chanting Section - Rounds (0-192)
  chantingQualityRounds?: number | null;
  
  // Body Section - Points (0, 5, 10, 15, 20, 25, 30)
  bodyToBed?: number | null;
  bodyEarlyMorning?: number | null;
  bodyDaySleep?: number | null;
  bodyJapaRounds?: number | null;
  bodyMangalaArti?: number | null;
  
  // Soul Section - Points (0, 5, 10, 15, 20, 25, 30)
  soulJapaQuality?: number | null;
  soulBookReading?: number | null;
  soulLectureHearing?: number | null;
  soulWeeklyClass?: number | null;
  
  // Duty Section - Points (0, 5, 10, 15, 20, 25, 30)
  dutyDeityWorship?: number | null;
  dutyAttendance?: number | null;
  
  // Seva Section - Minutes (not points)
  seva1Minutes?: number | null;
  seva1Description?: string | null;
  seva2Minutes?: number | null;
  seva2Description?: string | null;
  seva3Minutes?: number | null;
  seva3Description?: string | null;
  seva4Minutes?: number | null;
  seva4Description?: string | null;
  
  // Daily summary
  dailyRemarks?: string | null;
}

/**
 * Request body for submitting weekly card
 */
export interface SubmitWeeklySadhanaRequest {
  memberId?: number;
  weekStartDate: string; // YYYY-MM-DD (must be Sunday)
}

/**
 * Request body for mentor comment
 */
export interface MentorCommentRequest {
  cardId: number;
  comment: string; // max 1000 chars
}

/**
 * Response for submit action
 */
export interface SubmitWeeklySadhanaResponse {
  message: string;
  card: WeeklySadhanaCardSummary;
}

/**
 * Response for mentor comment action
 */
export interface MentorCommentResponse {
  message: string;
  card: WeeklySadhanaCardSummary;
}

/**
 * Paginated cards response
 * NOTE: Contains full WeeklySadhanaCard objects with all fields (weeklyStreak, bestDay, etc.)
 */
export interface WeeklySadhanaCardsResponse {
  cards: WeeklySadhanaCard[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * API Error response
 */
export interface SadhanaApiError {
  error: string;
  code?: string;
  details?: Record<string, any>;
}

/**
 * Day of week enum
 */
export enum DayOfWeek {
  Monday = 0,
  Tuesday = 1,
  Wednesday = 2,
  Thursday = 3,
  Friday = 4,
  Saturday = 5,
  Sunday = 6,
}

/**
 * Helper function to get day name from day number
 * JavaScript getDay() returns 0=Sunday, 1=Monday, 2=Tuesday, ..., 6=Saturday
 */
export function getDayName(day: number): string {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[day] || 'Unknown';
}

/**
 * Helper function to calculate total seva minutes
 */
export function calculateSevaTotalMinutes(entry: Partial<WeeklySadhanaEntry>): number {
  return (
    (entry.seva1Minutes || 0) +
    (entry.seva2Minutes || 0) +
    (entry.seva3Minutes || 0) +
    (entry.seva4Minutes || 0)
  );
}

/**
 * Helper function to get point label for display
 */
export function getPointLabel(points: number | null | undefined): string {
  if (points === null || points === undefined) return 'Not entered';
  switch (points) {
    case 0: return '0 pts (Not done)';
    case 5: return '5 pts (Poor)';
    case 10: return '10 pts (Below avg)';
    case 15: return '15 pts (Average)';
    case 20: return '20 pts (Good)';
    case 25: return '25 pts (Very good)';
    case 30: return '30 pts (Excellent)';
    default: return `${points} pts`;
  }
}

// ---------------------------------------------------------------------------
// Legacy template-based sadhana types (used by congregation admin pages)
// ---------------------------------------------------------------------------

export type SadhanaTemplateUnit = 'MARKS' | 'MINUTES' | 'BOOLEAN' | 'ROUNDS' | 'TIME' | 'UNKNOWN' | string;
export type SadhanaTemplateFrequency = 'DAILY' | 'WEEKLY' | string;
export type SadhanaApplyStrategy = 'IMMEDIATE' | 'NEXT_SUNDAY';
export type SadhanaApplyStrategyExtended = SadhanaApplyStrategy;

export interface SadhanaTemplateItem {
  id: number;
  templateId: number;
  section: string;
  title: string;
  description?: string | null;
  thresholds?: Record<string, unknown> | null;
  unit: SadhanaTemplateUnit;
  frequency: SadhanaTemplateFrequency;
  ruleJson?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SadhanaTemplate {
  id: number;
  name: string;
  description?: string | null;
  isDefault: boolean;
  isActive: boolean;
  version: number;
  createdBy?: number | null;
  updatedBy?: number | null;
  createdAt: string;
  updatedAt: string;
  items: SadhanaTemplateItem[];
}

export interface SadhanaTemplateAssignment {
  id: number;
  devoteeMemberId: number;
  templateId: number;
  mentorMemberId?: number | null;
  applyStrategy: SadhanaApplyStrategy;
  effectiveFrom: string;
  reason?: string | null;
  createdBy?: number | null;
  updatedBy?: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface SadhanaTemplateAssignmentRequest {
  templateId: number;
  applyStrategy: SadhanaApplyStrategy;
  effectiveFrom?: string | null;
  reason?: string | null;
}

export interface SadhanaTemplateListResponse { templates: SadhanaTemplate[] }
export interface SadhanaTemplateResponse { template: SadhanaTemplate }
export interface SadhanaAssignmentResponse { assignment: SadhanaTemplateAssignment; template: SadhanaTemplate }
export interface SadhanaCurrentAssignmentResponse { template: SadhanaTemplate; assignment?: SadhanaTemplateAssignment | null }
export interface SadhanaAssignmentsResponse { assignments: SadhanaTemplateAssignment[] }

export type SadhanaWeekStatus = 'IN_PROGRESS' | 'SUBMITTED' | 'LOCKED' | string;

export interface SadhanaCardWeek {
  id: number;
  devoteeMemberId: number;
  templateId: number;
  mentorAssignmentId?: number | null;
  weekStartDate: string;
  weekEndDate: string;
  status: SadhanaWeekStatus;
  prescribedRoundsSnapshot: number;
  roundsCompletedTotal: number;
  roundsComplianceDays: number;
  mentorComment?: string | null;
  mentorCommentedAt?: string | null;
  mentorCommentedByMemberId?: number | null;
  mentorCommentedByUserId?: number | null;
  lockedAt?: string | null;
  submittedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SadhanaLegacyEntry {
  id: number;
  weekId: number;
  itemId: number;
  entryDate: string;
  valueText?: string | null;
  valueNumber?: number | null;
  valueMinutes?: number | null;
  valueBool?: boolean | null;
  valueTime?: string | null;
  valueRounds?: number | null;
  completedTime?: string | null;
  marksAwarded: number;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SadhanaWeekListResponse { weeks: SadhanaCardWeek[]; hasMore: boolean; nextBefore?: string | null }
export interface SadhanaWeekDetailResponse { week: SadhanaCardWeek; template: SadhanaTemplate; entries: SadhanaLegacyEntry[] }
export interface SadhanaWeekSubmitResponse { week: SadhanaCardWeek }
export interface SadhanaWeekMentorCommentRequest { comment: string | null }
export interface SadhanaWeekMentorCommentResponse { week: SadhanaCardWeek }

export interface ChantingPrescription {
  id: number;
  devoteeMemberId: number;
  mentorMemberId?: number | null;
  prescribedRounds: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
  reason?: string | null;
  isException: boolean;
  createdBy?: number | null;
  updatedBy?: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface ChantingPrescriptionCreateRequest {
  prescribedRounds: number;
  applyStrategy: SadhanaApplyStrategyExtended;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  isException?: boolean;
  reason?: string | null;
}

export interface ChantingPrescriptionListResponse { prescriptions: ChantingPrescription[] }
export interface ChantingPrescriptionCreateResponse { prescription: ChantingPrescription; autoRevert?: ChantingPrescription | null }

export type SadhanaMigrationState = 'NOT_STARTED' | 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'PARTIAL' | 'FAILED';
export type SadhanaMigrationRunStatus = 'SUCCESS' | 'FAILED' | 'PARTIAL' | 'SKIPPED' | 'PENDING';
export type SadhanaMigrationArtifact = 'TEMPLATES' | 'WEEKS' | 'PRESCRIPTIONS';

export interface SadhanaMigrationStatus {
  memberId: number;
  state: SadhanaMigrationState;
  seeded: { templates: number; weeks: number; prescriptions: number };
  pending: { templates: number; weeks: number; prescriptions: number };
  lastRunId?: number | null;
  lastRunStatus?: SadhanaMigrationRunStatus | null;
  lastRunStartedAt?: string | null;
  lastRunCompletedAt?: string | null;
  lastSuccessfulRunId?: number | null;
  lastSuccessfulRunCompletedAt?: string | null;
  requiresAttention: boolean;
  issues?: string[] | null;
  lastErrorMessage?: string | null;
  lastSeededAt?: string | null;
}

export interface SadhanaMigrationRun {
  id: number;
  memberId: number;
  status: SadhanaMigrationRunStatus;
  startedAt: string;
  completedAt?: string | null;
  actorUserId?: number | null;
  actorMemberId?: number | null;
  artifactsProcessed?: number | null;
  artifactsInserted?: number | null;
  notes?: string | null;
  issues?: string[] | null;
  resumeToken?: string | null;
}

export interface SadhanaMigrationStatusResponse { status: SadhanaMigrationStatus }
export interface SadhanaMigrationRunListResponse { runs: SadhanaMigrationRun[]; hasMore: boolean; nextBefore?: string | null }
export interface SadhanaReseedRequest { reason?: string | null; seedMissingOnly?: boolean; artifacts?: SadhanaMigrationArtifact[] }
export interface SadhanaReseedResponse { jobId: string; status: SadhanaMigrationRunStatus; enqueuedAt: string; resumeToken?: string | null }

// Alias for backwards compat with admin portal (was SadhanaEntry)
export type SadhanaEntry = SadhanaLegacyEntry;
